import { FieldArray, FieldArrayRenderProps } from 'formik';
import Image from 'next/image';
import { FocusEventHandler, ReactElement, useEffect, useState } from 'react';
import { NextImageBasicData } from '../../../types/Next';

import styles from './BasicButtonSelectionInput.module.css';

export interface FieldArrayProps {
  id: string;
  disabled: boolean;
  allowUserInput?: boolean;
  isMultipleSelection?: boolean;
  label: JSX.Element;
  value: (string | undefined)[] | undefined | string;
  setValue?: (newValue: string) => void;
  defaultOptions: string[];
  defaultOptionIcons?: NextImageBasicData[];
  errors?: string | string[];
  // Formik element that triggers validation when blurred
  onBlur: FocusEventHandler<HTMLInputElement>;
  // Formik element that determines if a user has visited a field
  touched: boolean | undefined;
  center?: boolean;
  extraSmall?: boolean;
}

export const BasicButtonSelectionInput = ({
  id,
  disabled,
  allowUserInput = false,
  isMultipleSelection = false,
  label,
  value,
  setValue = () => {},
  defaultOptions,
  defaultOptionIcons,
  errors,
  onBlur,
  touched,
  center,
  extraSmall,
}: FieldArrayProps) => {
  const [userAddedOptions, setUserAddedOptions] = useState<string[]>([]);
  const [newListElement, setNewListElement] = useState<string>('');

  // Prepopulate userAddedOptions on initial render if values already exist
  useEffect(() => {
    if (value instanceof Array) {
      const newUserAddedOptions = [];
      for (let i = 0; i < value.length; i++) {
        const potentialUserAddedOption = value[i];
        // If this exists in the default values it is not a user option
        if (!potentialUserAddedOption) continue;
        if (defaultOptions.indexOf(potentialUserAddedOption) === -1) {
          newUserAddedOptions.push(potentialUserAddedOption);
        }
      }
      setUserAddedOptions(newUserAddedOptions);
    }
  }, [defaultOptions]);

  const listenToEnterKeypress = (e: KeyboardEvent) => {
    const addButton = document.getElementById(
      `${id}-add-button`
    ) as HTMLButtonElement;
    const input = document.getElementById(id) as HTMLInputElement;
    if (input && addButton && e.key === 'Enter') {
      e.preventDefault();
      addButton.click();
      if (isMultipleSelection) {
        setUserAddedOptions((currUserAddedOptions) => [
          ...currUserAddedOptions,
          input.value,
        ]);
      } else {
        setUserAddedOptions([input.value]);
      }
    }
  };

  useEffect(() => {
    const input = document.getElementById(id);
    if (input) {
      input.addEventListener('keypress', listenToEnterKeypress);
      return () => input.removeEventListener('keypress', listenToEnterKeypress);
    }
  }, [userAddedOptions]);

  // Delete user option
  const handleDeleteUserOptionClick = (
    arrayHelpers: FieldArrayRenderProps,
    text: string
  ) => {
    if (!isMultipleSelection) {
      // Remove the user option from the options
      setUserAddedOptions([]);
      // If the option was selected, reset the value
      if (value === text) {
        setValue('');
      }
    } else {
      // Multiple selection
      if (typeof value === 'undefined') return;
      const indexOfText = value.indexOf(text);
      const indexInUserAddedOptions = userAddedOptions.indexOf(text);
      if (indexOfText > -1) {
        // Remove the option from values if it was selected
        arrayHelpers.remove(indexOfText);
      }
      if (indexInUserAddedOptions > -1) {
        // Remove the user option from options
        const newUserAddedOptions = [...userAddedOptions];
        newUserAddedOptions.splice(indexInUserAddedOptions, 1);
        setUserAddedOptions(newUserAddedOptions);
      }
    }
  };

  // Add or remove option from values
  const handleOptionClick = (
    arrayHelpers: FieldArrayRenderProps,
    text: string
  ) => {
    // This is single selection
    if (!isMultipleSelection) {
      if (value === text) {
        // Option was previously selected, so reset value
        setValue('');
      } else {
        // Option has been newly selected, set value to text
        setValue(text);
      }
    } else {
      // This is multiple options
      if (typeof value === 'undefined') return;
      const indexOfText = value.indexOf(text);
      if (indexOfText > -1) {
        // Option was previously selected, so remove value
        arrayHelpers.remove(indexOfText);
      } else {
        // Option has been newly selected, add value
        arrayHelpers.push(text);
      }
    }
  };

  const getUserInput = (
    arrayHelpers: FieldArrayRenderProps
  ): ReactElement | null => {
    if (
      allowUserInput &&
      (isMultipleSelection || userAddedOptions.length === 0)
    ) {
      return (
        <div className={styles['options-enter-container']}>
          <input
            id={id}
            aria-label={`${id}-input`}
            aria-invalid={errors ? true : false}
            aria-errormessage={`${id}-error-message`}
            disabled={disabled}
            placeholder="type here..."
            value={newListElement}
            onChange={(e) => setNewListElement(e.target.value)}
            onBlur={onBlur}
            className={styles[extraSmall? 'input2' : 'input']}
          />
          <button
            id={`${id}-add-button`}
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              if (newListElement) {
                if (!isMultipleSelection) {
                  setValue(newListElement);
                } else {
                  arrayHelpers.push(newListElement);
                }
                setNewListElement('');
              }
            }}
            className={styles['enter-button']}
          >
            <p>Press Enter</p>{' '}
            <Image
              src="/icons/EnterArrowBlack.svg"
              alt="Press Enter"
              objectFit="contain"
              height={6}
              width={12}
            />
          </button>
        </div>
      );
    }

    return null;
  };

  const optionIsSelected = (option: string): boolean => {
    if (typeof value === 'undefined') return false;
    if (!isMultipleSelection) {
      return value === option;
    } else if (value) {
      const indexOfOption = value.indexOf(option);
      return indexOfOption > -1;
    }
    return false;
  };

  return (
    <FieldArray
      name={id}
      render={(arrayHelpers) => {
        return (
          <div className={styles['container']}>
            <form onSubmit={(e) => e.preventDefault()}>
              <label htmlFor={id} className={styles['label']}>
                {label}
              </label>
              <div
                aria-label={`${id}-text-list`}
                className={styles['options-grid']}
                style={{
                  justifyContent: center ? 'center' : 'flex-start',
                }}
              >
                {[...defaultOptions, ...userAddedOptions]?.map(
                  (text, index) => {
                    if (!text) return;
                    return (
                      <div
                        key={`${id}-${index}`}
                        className={styles[extraSmall ? 'option-container2' : 'option-container']}
                        style={{
                          backgroundColor: optionIsSelected(text)
                            ? 'var(--ea-gray)'
                            : 'var(--ea-white)',
                          borderWidth: optionIsSelected(text) ? 2 : 1,
                          opacity: disabled ? 0.5 : 1,
                        }}
                      >
                        <button
                          type="button"
                          aria-label={text}
                          disabled={disabled}
                          onClick={() => handleOptionClick(arrayHelpers, text)}
                          className={styles[extraSmall? 'option-text2' : 'option-text']}
                        >
                          {defaultOptionIcons && defaultOptionIcons[index] && (
                            <Image
                              alt=""
                              objectFit="contain"
                              {...defaultOptionIcons[index]}
                            />
                          )}
                          <p>{text}</p>
                        </button>
                        {defaultOptions.indexOf(text) === -1 && (
                          <button
                            disabled={disabled}
                            aria-label={`remove ${text}`}
                            onClick={() =>
                              handleDeleteUserOptionClick(arrayHelpers, text)
                            }
                            className={styles['option-x-button']}
                          >
                            <Image
                              src="/icons/CloseBlack.svg"
                              alt="delete"
                              objectFit="contain"
                              height={16}
                              width={16}
                            />
                          </button>
                        )}
                      </div>
                    );
                  }
                )}
                {getUserInput(arrayHelpers)}
              </div>
              {touched && errors && (
                <p
                  aria-label={`${id}-error-message`}
                  id={`${id}-error-message`}
                  className={styles['errors']}
                >
                  {errors}
                </p>
              )}
            </form>
          </div>
        );
      }}
    />
  );
};
